// Detects a slow/metered connection (Network Information API — Chrome/
// Android only, feature-detected; absent on Safari/Firefox) and exposes a
// single `isLite` flag the rest of the app degrades against: smaller/no
// decorative images, no hero carousel, no video autoplay, instant Framer
// Motion transitions. Detection alone is never trusted — a visitor can
// always flip it manually via the footer's "Lite mode" toggle, which
// persists across visits and overrides detection either way.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const LITE_MODE_KEY = 've_lite_mode';
const SLOW_EFFECTIVE_TYPES = new Set(['slow-2g', '2g', '3g']);

function getConnectionApi() {
  if (typeof navigator === 'undefined') return null;
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
}

function readNetworkInfo() {
  const conn = getConnectionApi();
  if (!conn) return { supported: false, saveData: false, effectiveType: null };
  return { supported: true, saveData: !!conn.saveData, effectiveType: conn.effectiveType || null };
}

function readManualPreference() {
  try {
    const raw = localStorage.getItem(LITE_MODE_KEY);
    return raw === null ? null : raw === 'true';
  } catch (_) {
    return null;
  }
}

const ConnectionContext = createContext(undefined);

export function ConnectionProvider({ children }) {
  const [network, setNetwork] = useState(readNetworkInfo);
  const [manualPreference, setManualPreference] = useState(readManualPreference);

  useEffect(() => {
    const conn = getConnectionApi();
    if (!conn) return undefined;
    const update = () => setNetwork(readNetworkInfo());
    conn.addEventListener('change', update);
    return () => conn.removeEventListener('change', update);
  }, []);

  const setManualLite = useCallback((value) => {
    setManualPreference(value);
    try {
      if (value === null) {
        localStorage.removeItem(LITE_MODE_KEY);
      } else {
        localStorage.setItem(LITE_MODE_KEY, value ? 'true' : 'false');
      }
    } catch (_) {
      // Storage unavailable — the toggle still works for this tab via state.
    }
  }, []);

  const detectedSlow = network.saveData || SLOW_EFFECTIVE_TYPES.has(network.effectiveType);
  // An explicit manual choice always wins over detection, in either direction.
  const isLite = manualPreference !== null ? manualPreference : detectedSlow;

  useEffect(() => {
    document.documentElement.setAttribute('data-connection', isLite ? 'slow' : 'normal');
  }, [isLite]);

  const value = {
    ...network,
    detectedSlow,
    isLite,
    manualPreference,
    setManualLite,
  };

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

export function useConnection() {
  const ctx = useContext(ConnectionContext);
  if (!ctx) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return ctx;
}
